import AppLayout from '@/Layouts/AppLayout';
import { Card, CardContent } from '@/Components/ui/card';
import { Section, SectionHeader } from '@/Components/Section';
import { FadeIn, StaggerChildren, staggerItem, motion } from '@/Components/motion';
import { Button } from '@/Components/ui/button';
import { Badge } from '@/Components/ui/badge';
import { Link } from 'react-router-dom';
import { siteConfig } from '@/config/site';
import {
    ArrowRight,
    Droplets,
    Clock,
    TrendingUp,
    DollarSign,
    Globe,
    BarChart3,
    Zap,
    Shield,
} from 'lucide-react';

const advantages = [
    {
        icon: Droplets,
        title: 'Unmatched Liquidity',
        description:
            'The forex market processes over $7.5 trillion in daily volume, making it the most liquid market in the world. This means tighter spreads, better fills, and the ability to enter and exit positions of any size with minimal slippage.',
        stat: '$7.5T',
        statLabel: 'Daily Volume',
    },
    {
        icon: Clock,
        title: '24/5 Market Access',
        description:
            'Unlike stock markets with limited hours, forex trades around the clock from Sunday evening to Friday night. This gives you the flexibility to trade around your schedule, whether you are a full-time trader or trading alongside a day job.',
        stat: '24/5',
        statLabel: 'Market Hours',
    },
    {
        icon: TrendingUp,
        title: 'Leverage Opportunities',
        description:
            `Forex offers significant leverage, allowing you to control large positions with a relatively small amount of capital. At ${siteConfig.brand.name}, our funded accounts provide up to 1:50 leverage, amplifying your profit potential while our risk framework keeps you protected.`,
        stat: '1:50',
        statLabel: 'Max Leverage',
    },
    {
        icon: DollarSign,
        title: 'Low Transaction Costs',
        description:
            'Forex trading typically involves lower costs than other financial markets. With no exchange fees, no clearing fees, and tight spreads, more of your profit stays in your pocket. Commission-based pricing ensures full transparency.',
        stat: '0.0',
        statLabel: 'Pips Spread From',
    },
    {
        icon: Globe,
        title: 'Global Accessibility',
        description:
            `All you need is an internet connection to access the forex market from anywhere in the world. Trade from your desktop, laptop, tablet, or smartphone with platforms like ${siteConfig.platform.name} that deliver a consistent experience across all devices.`,
        stat: '190+',
        statLabel: 'Countries',
    },
    {
        icon: BarChart3,
        title: 'Profit in Any Direction',
        description:
            'Unlike stocks where you primarily profit from price increases, forex allows you to trade both long and short with equal ease. Whether a currency is strengthening or weakening, there is always an opportunity to trade.',
        stat: '2-Way',
        statLabel: 'Trading',
    },
];

const additionalBenefits = [
    { icon: Zap, title: 'Fast Execution', description: 'Millisecond trade execution with minimal slippage on all major pairs.' },
    { icon: Shield, title: 'Regulated Market', description: 'The forex market is overseen by financial regulators worldwide.' },
    { icon: Globe, title: 'Diverse Instruments', description: 'Access major, minor, and exotic currency pairs plus crosses.' },
    { icon: TrendingUp, title: 'Technical Analysis', description: 'Forex markets are highly responsive to technical analysis patterns.' },
];

export default function AdvantagesOfForex() {
    return (
        <AppLayout title={`Advantages of Forex - ${siteConfig.brand.name}`} description="Discover the key advantages of trading forex and why it remains the world's largest financial market.">
            {/* Hero */}
            <Section className="pt-32 lg:pt-40 pb-20 bg-background">
                <div className="text-center max-w-4xl mx-auto">
                    <FadeIn>
                        <Badge className="mb-6">Forex Trading</Badge>
                    </FadeIn>
                    <FadeIn delay={0.1}>
                        <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-foreground mb-6 tracking-tight">
                            Advantages of <span className="text-gradient">Forex Trading</span>
                        </h1>
                    </FadeIn>
                    <FadeIn delay={0.2}>
                        <p className="text-lg text-muted-foreground max-w-2xl mx-auto leading-relaxed">
                            Forex is the world`s largest and most liquid financial market. Discover why millions of traders
                            worldwide choose forex as their primary trading instrument.
                        </p>
                    </FadeIn>
                </div>
            </Section>

            {/* Main Advantages */}
            <Section className="bg-muted/30 border-y border-border">
                <FadeIn>
                    <SectionHeader
                        title="Key"
                        highlight="Advantages"
                        description="The forex market offers unique benefits that make it the instrument of choice for professional traders."
                    />
                </FadeIn>
                <StaggerChildren>
                    <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
                        {advantages.map((advantage) => {
                            const Icon = advantage.icon;
                            return (
                                <motion.div key={advantage.title} variants={staggerItem}>
                                    <Card className="rounded-xl border border-border bg-card/80 backdrop-blur-sm hover:border-primary/20 transition-colors duration-300 p-0 h-full">
                                        <CardContent className="p-6 pt-6">
                                            <div className="flex items-center justify-between mb-4">
                                                <div className="flex h-12 w-12 items-center justify-center rounded-lg bg-primary/10 border border-primary/10">
                                                    <Icon className="h-6 w-6 text-primary" />
                                                </div>
                                                <div className="text-right">
                                                    <p className="text-2xl font-bold text-primary">{advantage.stat}</p>
                                                    <p className="text-xs text-muted-foreground">{advantage.statLabel}</p>
                                                </div>
                                            </div>
                                            <h3 className="text-lg font-semibold text-foreground mb-2">
                                                {advantage.title}
                                            </h3>
                                            <p className="text-sm text-muted-foreground leading-relaxed">
                                                {advantage.description}
                                            </p>
                                        </CardContent>
                                    </Card>
                                </motion.div>
                            );
                        })}
                    </div>
                </StaggerChildren>
            </Section>

            {/* Additional Benefits */}
            <Section className="bg-background">
                <FadeIn>
                    <SectionHeader
                        title="More"
                        highlight="Benefits"
                        description="Additional reasons why forex continues to attract traders of all experience levels."
                    />
                </FadeIn>
                <StaggerChildren>
                    <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6 max-w-5xl mx-auto">
                        {additionalBenefits.map((benefit) => {
                            const Icon = benefit.icon;
                            return (
                                <motion.div key={benefit.title} variants={staggerItem} className="text-center">
                                    <div className="mx-auto mb-4 flex h-14 w-14 items-center justify-center rounded-lg bg-primary/10 border border-primary/10">
                                        <Icon className="h-7 w-7 text-primary" />
                                    </div>
                                    <h3 className="font-semibold text-foreground mb-2">{benefit.title}</h3>
                                    <p className="text-sm text-muted-foreground leading-relaxed">{benefit.description}</p>
                                </motion.div>
                            );
                        })}
                    </div>
                </StaggerChildren>
            </Section>

            {/* CTA */}
            <Section className="bg-muted/30 border-y border-border">
                <FadeIn>
                    <div className="text-center max-w-3xl mx-auto">
                        <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold text-foreground mb-4 tracking-tight">
                            Start Trading Forex with <span className="text-gradient">{siteConfig.brand.name}</span>
                        </h2>
                        <p className="text-lg text-muted-foreground mb-8 leading-relaxed">
                            Take advantage of the world`s most liquid market with up to $100K in funded capital.
                            No risk to your own funds — just pure trading opportunity.
                        </p>
                        <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
                            <a href={siteConfig.portal.register} target="_blank" rel="noopener noreferrer">
                                <Button size="lg">
                                    Get Funded Today
                                    <ArrowRight className="ml-2 h-5 w-5" />
                                </Button>
                            </a>
                            <Link to="/funding/overview">
                                <Button variant="outline" size="lg">
                                    View Programs
                                </Button>
                            </Link>
                        </div>
                    </div>
                </FadeIn>
            </Section>
        </AppLayout>
    );
}
